import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { obfuscateEmail } from "@/core/helpers/obfuscateEmail";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { SoundButton } from "@/shared/ui";
import { SettingModal } from "@/domains/playerMenu/constants/settingModal";
import { safetySelector } from "@/domains/account/slice";
import PlayerMenuSettingsSafetyChangeEmailContainer from "@/domains/playerMenu/containers/settings/safety/change-email/PlayerMenuSettingsSafetyChangeEmailContainer";
import PlayerMenuSettingsSafetyChangePasswordContainer from "@/domains/playerMenu/containers/settings/safety/change-password/PlayerMenuSettingsSafetyChangePasswordContainer";
import PlayerMenuSettingsSafetyCloseSessionsContainer from "@/domains/playerMenu/containers/settings/safety/close-active-sessions/PlayerMenuSettingsSafetyCloseSessionsContainer";
import PlayerMenuSettingsSafetyDisableTfaAuthenticatorContainer from "@/domains/playerMenu/containers/settings/safety/tfa/PlayerMenuSettingsSafetyDisableTfaAuthenticatorContainer";
import PlayerMenuSettingsSafetyDisableTfaEmailContainer from "@/domains/playerMenu/containers/settings/safety/tfa/PlayerMenuSettingsSafetyDisableTfaEmailContainer";
import PlayerMenuSettingsSafetyDisableTfaSMSContainer from "@/domains/playerMenu/containers/settings/safety/tfa/PlayerMenuSettingsSafetyDisableTfaSMSContainer";
import PlayerMenuSettingsSafetyEnableTfaAuthenticatorContainer from "@/domains/playerMenu/containers/settings/safety/tfa/PlayerMenuSettingsSafetyEnableTfaAuthenticatorContainer";
import PlayerMenuSettingsSafetyEnableTfaEmailContainer from "@/domains/playerMenu/containers/settings/safety/tfa/PlayerMenuSettingsSafetyEnableTfaEmailContainer";
import PlayerMenuSettingsSafetyEnableTfaSMSContainer from "@/domains/playerMenu/containers/settings/safety/tfa/PlayerMenuSettingsSafetyEnableTfaSMSContainer";
import PlayerMenuContent from "@/domains/playerMenu/components/PlayerMenuContent";
import PlayerMenuContentTitle from "@/domains/playerMenu/components/PlayerMenuContentTitle";
import PlayerMenuSettingsSafetyTfaStatus from "@/domains/playerMenu/components/settings/safety/PlayerMenuSettingsSafetyTfaStatus";

const settingModalsInit: Record<SettingModal, boolean> = {
  [SettingModal.ConfirmCloseActiveSessions]: false,
  [SettingModal.EnableTfaSms]: false,
  [SettingModal.EnableTfaAuthenticator]: false,
  [SettingModal.EnableTfaEmail]: false,
  [SettingModal.DisableTfaSms]: false,
  [SettingModal.DisableTfaAuthenticator]: false,
  [SettingModal.DisableTfaEmail]: false,
  [SettingModal.ChangeEmail]: false,
  [SettingModal.ChangePassword]: false,
};

export default function PlayerMenuSettingsSafetyContainer() {
  const { t } = useTranslation("playerMenu");
  const { email, isTwoFactorEmailEnabled, isTwoFactorGoogleEnabled, isTwoFactorSmsEnabled } =
    useAppSelector(safetySelector);
  const [showSettingModals, setShowSettingModals] =
    useState<typeof settingModalsInit>(settingModalsInit);

  const closeSettingModalHandler = useCallback(
    (modal: SettingModal) =>
      setShowSettingModals({
        ...settingModalsInit,
        [modal]: false,
      }),
    [],
  );

  const openSettingModalHandler = useCallback(
    (modal: SettingModal) =>
      setShowSettingModals({
        ...settingModalsInit,
        [modal]: true,
      }),
    [],
  );

  const header = <PlayerMenuContentTitle>{t("playerMenu.tabTitle.Safety")}</PlayerMenuContentTitle>;

  const footer = (
    <div className="flex items-center justify-end">
      <SoundButton
        variant="soft"
        color="gray"
        className="h-[2.75rem]"
        onClick={() => openSettingModalHandler(SettingModal.ConfirmCloseActiveSessions)}
      >
        Завершить активные сессии
      </SoundButton>
    </div>
  );

  return (
    <PlayerMenuContent header={header} footer={footer}>
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <div className="h-[3.125rem] px-3.5 flex items-center gap-3.5 bg-dark-800 border border-dark-700/20 rounded-[0.5rem]">
            <div className="text-12 font-bold text-white uppercase">2FA Authenticator</div>
            <PlayerMenuSettingsSafetyTfaStatus
              isOn={isTwoFactorGoogleEnabled}
              className="ml-auto"
            />
            <SoundButton
              color={isTwoFactorGoogleEnabled ? undefined : "gray"}
              variant={isTwoFactorGoogleEnabled ? "solid-gradient" : "soft"}
              className="h-[2.25rem] w-[7.5rem]"
              onClick={() =>
                isTwoFactorGoogleEnabled
                  ? openSettingModalHandler(SettingModal.DisableTfaAuthenticator)
                  : openSettingModalHandler(SettingModal.EnableTfaAuthenticator)
              }
            >
              {t(`playerMenu.${isTwoFactorGoogleEnabled ? "turnOff" : "turnOn"}`)}
            </SoundButton>
          </div>
          <div className="h-[3.125rem] px-3.5 flex items-center gap-3.5 bg-dark-800 border border-dark-700/20 rounded-[0.5rem]">
            <div className="text-12 font-bold text-white uppercase">2FA EMAIL</div>
            <PlayerMenuSettingsSafetyTfaStatus
              isOn={isTwoFactorGoogleEnabled}
              className="ml-auto"
            />
            <SoundButton
              color={isTwoFactorEmailEnabled ? undefined : "gray"}
              variant={isTwoFactorEmailEnabled ? "solid-gradient" : "soft"}
              className="h-[2.25rem] w-[7.5rem]"
              onClick={() =>
                isTwoFactorEmailEnabled
                  ? openSettingModalHandler(SettingModal.DisableTfaEmail)
                  : openSettingModalHandler(SettingModal.EnableTfaEmail)
              }
            >
              {t(`playerMenu.${isTwoFactorEmailEnabled ? "turnOff" : "turnOn"}`)}
            </SoundButton>
          </div>
          <div className="h-[3.125rem] px-3.5 flex items-center gap-3.5 bg-dark-800 border border-dark-700/20 rounded-[0.5rem]">
            <div className="text-12 font-bold text-white uppercase">2FA SMS</div>
            <PlayerMenuSettingsSafetyTfaStatus isOn={isTwoFactorSmsEnabled} className="ml-auto" />
            <SoundButton
              color={isTwoFactorSmsEnabled ? undefined : "gray"}
              variant={isTwoFactorSmsEnabled ? "solid-gradient" : "soft"}
              className="h-[2.25rem] w-[7.5rem]"
              onClick={() =>
                isTwoFactorSmsEnabled
                  ? openSettingModalHandler(SettingModal.DisableTfaSms)
                  : openSettingModalHandler(SettingModal.EnableTfaSms)
              }
            >
              {t(`playerMenu.${isTwoFactorSmsEnabled ? "turnOff" : "turnOn"}`)}
            </SoundButton>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="h-[3.125rem] px-3.5 flex items-center gap-3.5 bg-dark-800 border border-dark-700/20 rounded-[0.5rem]">
            <div className="text-12 font-bold text-white uppercase">EMAIL</div>
            <div className="text-14 text-white/70 font-medium ml-auto">{obfuscateEmail(email)}</div>
            <SoundButton
              color="gray"
              variant="soft"
              className="h-[2.25rem] w-[7.5rem]"
              onClick={() => openSettingModalHandler(SettingModal.ChangeEmail)}
            >
              {t("playerMenu.change")}
            </SoundButton>
          </div>
          <div className="h-[3.125rem] px-3.5 flex items-center gap-3.5 bg-dark-800 border border-dark-700/20 rounded-[0.5rem]">
            <div className="text-12 font-bold text-white uppercase">{t("playerMenu.password")}</div>
            <div className="text-14 text-white/70 font-medium ml-auto">*************</div>
            <SoundButton
              color="gray"
              variant="soft"
              className="h-[2.25rem] w-[7.5rem]"
              onClick={() => openSettingModalHandler(SettingModal.ChangePassword)}
            >
              {t("playerMenu.change")}
            </SoundButton>
          </div>
        </div>
      </div>
      {showSettingModals[SettingModal.ConfirmCloseActiveSessions] && (
        <PlayerMenuSettingsSafetyCloseSessionsContainer
          onClose={() => closeSettingModalHandler(SettingModal.ConfirmCloseActiveSessions)}
        />
      )}
      {showSettingModals[SettingModal.EnableTfaSms] && (
        <PlayerMenuSettingsSafetyEnableTfaSMSContainer
          onClose={() => closeSettingModalHandler(SettingModal.EnableTfaSms)}
        />
      )}
      {showSettingModals[SettingModal.EnableTfaEmail] && (
        <PlayerMenuSettingsSafetyEnableTfaEmailContainer
          onClose={() => closeSettingModalHandler(SettingModal.EnableTfaEmail)}
        />
      )}
      {showSettingModals[SettingModal.EnableTfaAuthenticator] && (
        <PlayerMenuSettingsSafetyEnableTfaAuthenticatorContainer
          onClose={() => closeSettingModalHandler(SettingModal.EnableTfaAuthenticator)}
        />
      )}
      {showSettingModals[SettingModal.DisableTfaSms] && (
        <PlayerMenuSettingsSafetyDisableTfaSMSContainer
          onClose={() => closeSettingModalHandler(SettingModal.DisableTfaSms)}
        />
      )}
      {showSettingModals[SettingModal.DisableTfaEmail] && (
        <PlayerMenuSettingsSafetyDisableTfaEmailContainer
          onClose={() => closeSettingModalHandler(SettingModal.DisableTfaEmail)}
        />
      )}
      {showSettingModals[SettingModal.DisableTfaAuthenticator] && (
        <PlayerMenuSettingsSafetyDisableTfaAuthenticatorContainer
          onClose={() => closeSettingModalHandler(SettingModal.DisableTfaAuthenticator)}
        />
      )}
      {showSettingModals[SettingModal.ChangeEmail] && (
        <PlayerMenuSettingsSafetyChangeEmailContainer
          onClose={() => closeSettingModalHandler(SettingModal.ChangeEmail)}
        />
      )}
      {showSettingModals[SettingModal.ChangePassword] && (
        <PlayerMenuSettingsSafetyChangePasswordContainer
          onClose={() => closeSettingModalHandler(SettingModal.ChangePassword)}
        />
      )}
    </PlayerMenuContent>
  );
}
